import {type DragEvent, useState} from 'react';

type UseDragOverOptions = {
  onDragStart?: (evt: DragEvent) => void;
  onDrop?: (evt: DragEvent) => void;
};

/**
 * A hook for managing the drag-over state of an element. It wraps drag-start
 * and drop events, and tracks whether the element that is being dragged is
 * being dragged over the target element. If it is, `isDraggingOver` will be
 * true.
 *
 * This hook has some trickery in it to prevent multiple `onDrop` handlers from
 * being triggered by the same event. It does this by marking the event as
 * handled, which is checked by the `onDrop` handler. This requires calling
 * .persist() on the event object, which is a React-specific method that
 * tells it to use the same event instance for every event handler in the chain.
 */
export function useDragOver(opts?: UseDragOverOptions) {
  const {onDragStart, onDrop} = opts || {};
  // Count leave/enter events, because they fire in pairs when we drag over our
  // own children's boundaries.
  const [draggingOver, setDraggingOver] = useState(0);

  function handleDragStart(e: DragEvent) {
    e.dataTransfer.setData('text', ''); // this is necessary for drag+drop to work in firefox
    if (onDragStart) {
      onDragStart(e);
    }
  }
  function handleDragOver(e: DragEvent) {
    e.preventDefault(); // this is necessary for onDrop to work
  }
  function handleDrop(e: DragEvent) {
    setDraggingOver(0);

    // @ts-expect-error
    if (onDrop && !e.handled) {
      // Mark the onDrop event as handled to prevent multiple handlers from
      // being triggered by this event. We can't use stopPropagation because
      // that prevents other useDragOver hooks from receiving the event, and
      // they don't properly set `draggingOver` to 0. We can't use
      // preventDefault and isDefaultPrevented, because that intercepts the
      // browser's default drag/drop behavior.
      //
      // .persist() is necessary to allow us to modify the event object:
      e.persist();
      // @ts-expect-error
      e.handled = true; // mark the event as handled
      onDrop(e);
    }
  }
  function handleDragEnter() {
    setDraggingOver(prev => prev + 1);
  }
  function handleDragleave() {
    setDraggingOver(prev => prev - 1);
  }

  const getDraggingProps = () => {
    return {
      onDragStart: handleDragStart,
      onDragOver: handleDragOver,
      onDrop: handleDrop,
      onDragEnter: handleDragEnter,
      onDragLeave: handleDragleave,
    };
  };

  return {getDraggingProps, isDraggingOver: draggingOver > 0};
}
